import { type FC } from "react";
import Text from "../Text";
import { KrunshScoreTextBlockType, type KrunshScoreDetails } from "@/API";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTranslation } from "react-i18next";
import { getColorKrunshScore } from "@/lib/utils";
import Badge from "../Badge";

type Props = {
  krunshScore: number;
  krunshScoreDetails: KrunshScoreDetails;
  isBlured?: boolean;
};

const getColorBadge = (text: string) => {
  if (text === "krunsh_score.badge.normal") return "success";
  if (text === "krunsh_score.badge.underxploited") return "warning";
  return "error";
};

const getColorText = (text: string) => {
  if (text === "krunsh_score.badge.normal") return "#022D0D";
  if (text === "krunsh_score.badge.underxploited") return "#702E00";
  return "#750E13";
};

const KrunshScoreCard: FC<Props> = ({
  krunshScore,
  krunshScoreDetails,
  isBlured,
}) => {
  const { t } = useTranslation();
  const {
    ratingCount,
    deliveryTime,
    rating,
    productAvailability,
    productImages,
  } = krunshScoreDetails;

  return (
    <div
      className="bg-[white] p-[16px] rounded-[16px] h-[fit-content]"
      style={
        isBlured
          ? {
              filter: "blur(4px)",
            }
          : undefined
      }
    >
      <div className="flex gap-[12px] items-center mb-[26px]">
        <div
          className="bg-[white] h-[40px] min-h-[80px] w-[80px] min-w-[80px] rounded-[50%] flex items-center justify-center"
          style={{ border: `solid 4px ${getColorKrunshScore(krunshScore)}` }}
        >
          <Text
            fontWeight={600}
            fontSize={32}
            color={getColorKrunshScore(krunshScore)}
          >
            {krunshScore}
          </Text>
        </div>

        <Text fontWeight={600} fontSize={20}>
          {t("krunsh_score")}
        </Text>
      </div>

      <Text fontSize={16} fontWeight={600}>
        {t("krunsh_score_details_and_recommendations")}
      </Text>

      <Accordion
        type="single"
        collapsible
        className="mt-[16px]"
        disabled={isBlured}
      >
        <AccordionItem value="ratingCount">
          <AccordionTrigger className="items-center">
            <div className="flex items-center gap-[12px]">
              <div
                className="bg-[white] h-[40px] min-h-[40px] w-[40px] min-w-[40px] rounded-[50%] flex items-center justify-center"
                style={{
                  border: `solid 2px ${getColorKrunshScore(ratingCount.value)}`,
                }}
              >
                <Text
                  fontWeight={600}
                  fontSize={16}
                  color={getColorKrunshScore(ratingCount.value)}
                >
                  {ratingCount.value}
                </Text>
              </div>
              <div className="flex flex-col">
                <Text fontWeight={500} fontSize={14}>
                  {t("commercial_attractiveness")}
                </Text>
                <Badge state={getColorBadge(ratingCount.badge)}>
                  <Text
                    fontSize={12}
                    fontWeight={600}
                    color={getColorText(ratingCount.badge)}
                  >
                    {t(ratingCount.badge)}
                  </Text>
                </Badge>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div
              className="flex flex-col p-[16px] gap-[10px] bg-[#FCFCFD] rounded-[12px]"
              style={{ border: "solid 1px #F1F2F4" }}
            >
              {ratingCount.text.map((krunshScoreTextBlock) => {
                if (!krunshScoreTextBlock) return null;

                return (
                  <div className="flex flex-col">
                    <Text fontSize={14} fontWeight={600} color="#44546F">
                      {t(krunshScoreTextBlock.title)}
                    </Text>
                    {krunshScoreTextBlock.items.map((text) => {
                      if (!text) return null;
                      if (
                        krunshScoreTextBlock.type ===
                        KrunshScoreTextBlockType.TEXT
                      )
                        return <Text>{t(text)}</Text>;
                      return (
                        <div className="flex items-center gap-[12px]">
                          <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                          <Text
                            fontSize={14}
                            fontWeight={400}
                            style={{ lineHeight: "24px" }}
                          >
                            {t(text)}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="productImages">
          <AccordionTrigger className="items-center">
            <div className="flex items-center gap-[12px]">
              <div
                className="bg-[white] h-[40px] min-h-[40px] w-[40px] min-w-[40px] rounded-[50%] flex items-center justify-center"
                style={{
                  border: `solid 2px ${getColorKrunshScore(productImages.value)}`,
                }}
              >
                <Text
                  fontWeight={600}
                  fontSize={16}
                  color={getColorKrunshScore(productImages.value)}
                >
                  {productImages.value}
                </Text>
              </div>
              <div className="flex flex-col">
                <Text fontWeight={500} fontSize={14}>
                  {t("visual_attractiveness")}
                </Text>
                <Badge state={getColorBadge(productImages.badge)}>
                  <Text
                    fontSize={12}
                    fontWeight={600}
                    color={getColorText(productImages.badge)}
                  >
                    {t(productImages.badge)}
                  </Text>
                </Badge>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div
              className="flex flex-col p-[16px] gap-[10px] bg-[#FCFCFD] rounded-[12px]"
              style={{ border: "solid 1px #F1F2F4" }}
            >
              {productImages.text.map((krunshScoreTextBlock) => {
                if (!krunshScoreTextBlock) return null;

                return (
                  <div className="flex flex-col">
                    <Text fontSize={14} fontWeight={600} color="#44546F">
                      {t(krunshScoreTextBlock.title)}
                    </Text>
                    {krunshScoreTextBlock.items.map((text) => {
                      if (!text) return null;
                      if (
                        krunshScoreTextBlock.type ===
                        KrunshScoreTextBlockType.TEXT
                      )
                        return <Text>{t(text)}</Text>;
                      return (
                        <div className="flex items-center gap-[12px]">
                          <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                          <Text
                            fontSize={14}
                            fontWeight={400}
                            style={{ lineHeight: "24px" }}
                          >
                            {t(text)}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="deliveryTime">
          <AccordionTrigger className="items-center">
            <div className="flex items-center gap-[12px]">
              <div
                className="bg-[white] h-[40px] min-h-[40px] w-[40px] min-w-[40px] rounded-[50%] flex items-center justify-center"
                style={{
                  border: `solid 2px ${getColorKrunshScore(deliveryTime.value)}`,
                }}
              >
                <Text
                  fontWeight={600}
                  fontSize={16}
                  color={getColorKrunshScore(deliveryTime.value)}
                >
                  {deliveryTime.value}
                </Text>
              </div>
              <div className="flex flex-col">
                <Text fontWeight={500} fontSize={14}>
                  {t("operational_excellence")}
                </Text>
                <Badge state={getColorBadge(deliveryTime.badge)}>
                  <Text
                    fontSize={12}
                    fontWeight={600}
                    color={getColorText(deliveryTime.badge)}
                  >
                    {t(deliveryTime.badge)}
                  </Text>
                </Badge>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div
              className="flex flex-col p-[16px] gap-[10px] bg-[#FCFCFD] rounded-[12px]"
              style={{ border: "solid 1px #F1F2F4" }}
            >
              {deliveryTime.text.map((krunshScoreTextBlock) => {
                if (!krunshScoreTextBlock) return null;

                return (
                  <div className="flex flex-col">
                    <Text fontSize={14} fontWeight={600} color="#44546F">
                      {t(krunshScoreTextBlock.title)}
                    </Text>
                    {krunshScoreTextBlock.items.map((text) => {
                      if (!text) return null;
                      if (
                        krunshScoreTextBlock.type ===
                        KrunshScoreTextBlockType.TEXT
                      )
                        return <Text>{t(text)}</Text>;
                      return (
                        <div className="flex items-center gap-[12px]">
                          <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                          <Text
                            fontSize={14}
                            fontWeight={400}
                            style={{ lineHeight: "24px" }}
                          >
                            {t(text)}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="rating">
          <AccordionTrigger className="items-center">
            <div className="flex items-center gap-[12px]">
              <div
                className="bg-[white] h-[40px] min-h-[40px] w-[40px] min-w-[40px] rounded-[50%] flex items-center justify-center"
                style={{
                  border: `solid 2px ${getColorKrunshScore(rating.value)}`,
                }}
              >
                <Text
                  fontWeight={600}
                  fontSize={16}
                  color={getColorKrunshScore(rating.value)}
                >
                  {rating.value}
                </Text>
              </div>
              <div className="flex flex-col">
                <Text fontWeight={500} fontSize={14}>
                  {t("customer_satisfaction")}
                </Text>
                <Badge state={getColorBadge(rating.badge)}>
                  <Text
                    fontSize={12}
                    fontWeight={600}
                    color={getColorText(rating.badge)}
                  >
                    {t(rating.badge)}
                  </Text>
                </Badge>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div
              className="flex flex-col p-[16px] gap-[10px] bg-[#FCFCFD] rounded-[12px]"
              style={{ border: "solid 1px #F1F2F4" }}
            >
              {rating.text.map((krunshScoreTextBlock) => {
                if (!krunshScoreTextBlock) return null;

                return (
                  <div className="flex flex-col">
                    <Text fontSize={14} fontWeight={600} color="#44546F">
                      {t(krunshScoreTextBlock.title)}
                    </Text>
                    {krunshScoreTextBlock.items.map((text) => {
                      if (!text) return null;
                      if (
                        krunshScoreTextBlock.type ===
                        KrunshScoreTextBlockType.TEXT
                      )
                        return <Text>{t(text)}</Text>;
                      return (
                        <div className="flex items-center gap-[12px]">
                          <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                          <Text
                            fontSize={14}
                            fontWeight={400}
                            style={{ lineHeight: "24px" }}
                          >
                            {t(text)}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="productAvailability">
          <AccordionTrigger className="items-center">
            <div className="flex items-center gap-[12px]">
              <div
                className="bg-[white] h-[40px] min-h-[40px] w-[40px] min-w-[40px] rounded-[50%] flex items-center justify-center"
                style={{
                  border: `solid 2px ${getColorKrunshScore(productAvailability.value)}`,
                }}
              >
                <Text
                  fontWeight={600}
                  fontSize={16}
                  color={getColorKrunshScore(productAvailability.value)}
                >
                  {productAvailability.value}
                </Text>
              </div>
              <div className="flex flex-col">
                <Text fontWeight={500} fontSize={14}>
                  {t("product_availability")}
                </Text>
                <Badge state={getColorBadge(productAvailability.badge)}>
                  <Text
                    fontSize={12}
                    fontWeight={600}
                    color={getColorText(productAvailability.badge)}
                  >
                    {t(productAvailability.badge)}
                  </Text>
                </Badge>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div
              className="flex flex-col p-[16px] gap-[10px] bg-[#FCFCFD] rounded-[12px]"
              style={{ border: "solid 1px #F1F2F4" }}
            >
              {productAvailability.text.map((krunshScoreTextBlock) => {
                if (!krunshScoreTextBlock) return null;

                return (
                  <div className="flex flex-col">
                    <Text fontSize={14} fontWeight={600} color="#44546F">
                      {t(krunshScoreTextBlock.title)}
                    </Text>
                    {krunshScoreTextBlock.items.map((text) => {
                      if (!text) return null;
                      if (
                        krunshScoreTextBlock.type ===
                        KrunshScoreTextBlockType.TEXT
                      )
                        return <Text>{t(text)}</Text>;
                      return (
                        <div className="flex items-center gap-[12px]">
                          <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                          <Text
                            fontSize={14}
                            fontWeight={400}
                            style={{ lineHeight: "24px" }}
                          >
                            {t(text)}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default KrunshScoreCard;
